import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
    Mail,
    Phone,
    MapPin,
    Clock,
    Send,
    CheckCircle,
} from 'lucide-react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);
    const navigate = useNavigate();

    const services = [
        'Network Infrastructure Buildout',
        'Global Connectivity Solutions',
        'Audio-Visual Solutions',
        'Building Management Systems',
        'Business Process Automation & Managed Services',
        'On-Grid & Off-Grid Power Solutions',
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleServiceChange = (value) => {
        setFormData((prev) => ({ ...prev, service_interest: value }));
    };

    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!recaptchaToken) {
            toast.error('Please complete the reCAPTCHA verification.');
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post(`${API}/contact`, {
                ...formData,
                recaptcha_token: recaptchaToken,
            });
            setIsSubmitted(true);
            // Track form submission in Google Tag Manager - fires only after successful API response
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'form_submit' });
                console.log('dataLayer event pushed: form_submit');
            }
            // Redirect to Thank You page
            navigate('/thank-you');
        } catch (error) {
            console.error('Contact form error:', error);
            if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Something went wrong. Please try again.');
            }
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            setRecaptchaToken(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: MapPin,
            title: 'Our Office',
            content: 'Vanfrank Place | 9, Olaribiro Drive, Off Allen Avenue, Ikeja,\nLagos State, Nigeria',
        },
        {
            icon: Phone,
            title: 'Phone',
            content: '+234 201 227 4476',
            href: 'tel:+2342012274476',
            trackEvent: 'phone_call_click',
        },
        {
            icon: Mail,
            title: 'Email',
            content: 'lagos@vanfranklimited.com',
            href: 'mailto:lagos@vanfranklimited.com',
            trackEvent: 'email_click',
        },
        {
            icon: Clock,
            title: 'Business Hours',
            content: 'Mon - Fri: 9:00 AM - 6:00 PM',
        },
    ];

    return (
        <div data-testid="contact-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/30"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Get In Touch
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="contact-hero-title"
                        >
                            Contact Us
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Have a project in mind?
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Main Contact Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="bg-white rounded-2xl p-8 md:p-10 border border-slate-100 shadow-xl">
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                    Send Us a Message
                                </h2>
                                <p className="text-slate-600 mb-8">
                                    Fill out the form below and our team will get back to you within
                                    24 hours.
                                </p>

                                {isSubmitted ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-12"
                                        data-testid="contact-success"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                                            <CheckCircle className="text-green-500" size={32} />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                                            Message Sent!
                                        </h3>
                                        <p className="text-slate-600 mb-6">
                                            Thank you for reaching out. We'll be in touch soon.
                                        </p>
                                        <Button
                                            onClick={() => setIsSubmitted(false)}
                                            variant="outline"
                                            data-testid="send-another-btn"
                                        >
                                            Send Another Message
                                        </Button>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                                        <div className="grid sm:grid-cols-2 gap-6">
                                            <div>
                                                <label
                                                    htmlFor="name"
                                                    className="block text-sm font-medium text-slate-700 mb-2"
                                                >
                                                    Full Name *
                                                </label>
                                                <Input
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="John Doe"
                                                    data-testid="contact-name-input"
                                                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                                                />
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-slate-700 mb-2"
                                                >
                                                    Email Address *
                                                </label>
                                                <Input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="john@company.com"
                                                    data-testid="contact-email-input"
                                                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid sm:grid-cols-2 gap-6">
                                            <div>
                                                <label
                                                    htmlFor="company"
                                                    className="block text-sm font-medium text-slate-700 mb-2"
                                                >
                                                    Company Name
                                                </label>
                                                <Input
                                                    id="company"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleInputChange}
                                                    placeholder="Your Company"
                                                    data-testid="contact-company-input"
                                                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                                                />
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="phone"
                                                    className="block text-sm font-medium text-slate-700 mb-2"
                                                >
                                                    Phone Number
                                                </label>
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="+234 123 456 7890"
                                                    data-testid="contact-phone-input"
                                                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="service_interest"
                                                className="block text-sm font-medium text-slate-700 mb-2"
                                            >
                                                Service of Interest
                                            </label>
                                            <Select
                                                value={formData.service_interest}
                                                onValueChange={handleServiceChange}
                                            >
                                                <SelectTrigger
                                                    data-testid="contact-service-select"
                                                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                                                >
                                                    <SelectValue placeholder="Select a service" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {services.map((service) => (
                                                        <SelectItem key={service} value={service}>
                                                            {service}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="message"
                                                className="block text-sm font-medium text-slate-700 mb-2"
                                            >
                                                Message *
                                            </label>
                                            <Textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                required
                                                rows={5}
                                                placeholder="Tell us about your project or requirements..."
                                                data-testid="contact-message-input"
                                                className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 resize-none"
                                            />
                                        </div>

                                        <div className="flex justify-center">
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey={RECAPTCHA_SITE_KEY}
                                                onChange={handleRecaptchaChange}
                                                data-testid="contact-recaptcha"
                                            />
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            data-testid="contact-submit-btn"
                                            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 font-semibold shadow-[0_0_20px_rgba(15,23,42,0.3)] transition-all duration-300"
                                        >
                                            {isSubmitting ? (
                                                <span className="flex items-center gap-2">
                                                    <svg
                                                        className="animate-spin h-5 w-5"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                            fill="none"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        />
                                                    </svg>
                                                    Sending...
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-2">
                                                    <Send size={18} />
                                                    Send Message
                                                </span>
                                            )}
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </motion.div>

                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                                    Contact Information
                                </span>
                                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                                    Let's Have a Conversation
                                </h2>
                                <p className="text-slate-600 leading-relaxed mb-6">
                                    Whether you have a question about our services, want to discuss
                                    a potential project, or just want to learn more about how we can
                                    help, we're here to assist.
                                </p>
                                <div className="flex items-center gap-4">
                                    <a
                                        href="https://vanfrank.3cx.ng/LiveChat566122"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-talk2direct"
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors text-sm font-medium"
                                        onClick={() => {
                                            if (window.dataLayer) {
                                                window.dataLayer.push({ event: 'whatsapp_click' });
                                            }
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                            <path d="M14.05 2a9 9 0 0 1 8 7.94"></path>
                                            <path d="M14.05 6A5 5 0 0 1 18 10"></path>
                                        </svg>
                                        Talk2Direct
                                    </a>
                                </div>
                            </div>

                            <div data-testid="contact-info" className="space-y-6">
                                {contactInfo.map((item, index) => (
                                    <motion.div
                                        key={item.title}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-4 p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                                            <item.icon className="text-white" size={22} />
                                        </div>
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                className="text-slate-700 hover:text-blue-500 transition-colors font-medium"
                                                onClick={() => {
                                                    if (item.trackEvent && window.dataLayer) {
                                                        window.dataLayer.push({ event: item.trackEvent });
                                                    }
                                                }}
                                            >
                                                {item.content}
                                            </a>
                                        ) : (
                                        <p className="text-slate-700 font-medium whitespace-pre-line">{item.content}</p>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
